/**
 * Numeros, unidades e datas no formato do manual (p.23).
 *
 * Ponto de milhar, virgula decimal, relogio de 24 h. `4.832 h`, `R$ 320,50/h`,
 * `28.560 km`, `12/06/2025 · 14:32`. Nunca `4832h`, nunca data americana.
 *
 * A unidade sai separada do numero de proposito: na tela ela vai em cinza e sem
 * negrito, e para isso precisa ser um elemento proprio (ver `Medida`).
 */

const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

const DECIMAL = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DECIMAL_UMA_CASA = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const ATE_DUAS_CASAS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

const MOEDA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const DATA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const HORA = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function formatarInteiro(valor: number): string {
  return INTEIRO.format(valor);
}

export function formatarDecimal(valor: number, casas: 1 | 2 = 2): string {
  return casas === 1 ? DECIMAL_UMA_CASA.format(valor) : DECIMAL.format(valor);
}

/**
 * `12`, `0,5`, `0,35` — o numero com as casas que ele TEM, ate duas.
 *
 * Para o valor cadastrado que tanto pode ser inteiro quanto fracao: a faixa de
 * consumo, que o banco guarda em `numeric(8,2)`. `formatarInteiro` escrevia a
 * faixa de 0,3 a 0,6 L/km de um caminhao como "0 a 1", e a tela chegou a dizer
 * que 0,0 L/km estava abaixo dela. `formatarDecimal` de casas fixas erraria
 * pelo outro lado: a faixa de 12 a 22 L/h viraria "12,00 a 22,00".
 */
export function formatarAteDuasCasas(valor: number): string {
  return ATE_DUAS_CASAS.format(valor);
}

/**
 * A faixa como o cadastro a mostra: `0,3 a 0,6`, cada ponta com as casas que
 * tem. A ponta que falta e `—`, e nunca zero: "0 a 22" afirma um piso que
 * ninguem cadastrou. `null` quando nao ha ponta nenhuma, e o estado vazio e de
 * quem desenha.
 */
export function formatarFaixa(minimo: number | null, maximo: number | null): string | null {
  if (minimo === null && maximo === null) return null;
  const ponta = (valor: number | null) => (valor === null ? '—' : formatarAteDuasCasas(valor));
  return `${ponta(minimo)} a ${ponta(maximo)}`;
}

export type UnidadeDeConsumo = 'L/h' | 'L/km';

/**
 * A unidade do consumo, que e a do MEDIDOR: `L/km` para quem roda com
 * odometro, `L/h` para quem trabalha com horimetro.
 *
 * As duas nunca se somam nem se comparam: 0,5 L/km de caminhao e 18 L/h de
 * escavadeira nao sao o mesmo fato. Medidor desconhecido cai em `L/h`, a mesma
 * queda que a lista de consumo por maquina ja fazia.
 */
export function unidadeDeConsumo(tipoMedidor: string | null | undefined): UnidadeDeConsumo {
  return tipoMedidor === 'odometro' ? 'L/km' : 'L/h';
}

/** `R$ 320,50` — o `/h` e sufixo de unidade, nao faz parte da moeda. */
export function formatarReais(valor: number): string {
  return MOEDA.format(valor);
}

/**
 * `R$ 58,4 mil`, `R$ 1,2 mi`, `R$ 820,00` — o valor grande, curto, para o
 * indicador da ficha (R06, p.71) e o custo parado da Visão do gestor (p.72).
 * Abaixo de mil, o valor inteiro, com centavos.
 */
export function formatarReaisCompacto(valor: number): string {
  const abs = Math.abs(valor);
  const sinal = valor < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sinal}R$ ${formatarDecimal(abs / 1_000_000, 1)} mi`;
  if (abs >= 1_000) return `${sinal}R$ ${formatarDecimal(abs / 1_000, 1)} mil`;
  return formatarReais(valor);
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'] as const;

/** `Setembro de 2026`, a partir de `aaaa-mm-dd` (lido como texto, sem fuso). */
export function formatarMesPorExtenso(texto: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(texto);
  const nome = m ? MESES[Number(m[2]) - 1] : undefined;
  return m && nome ? `${nome} de ${m[1] ?? ''}` : DATA_INVALIDA;
}

/**
 * O que a tela escreve no lugar de uma data que nao e data.
 *
 * `Intl.DateTimeFormat.format` LEVANTA `RangeError` com `Invalid Date`, e um
 * formatador chamado de dentro de uma celula de tabela derrubava a tela
 * inteira ("Invalid time value") por causa de um campo so. A celula mostra o
 * marcador; o resto da tela continua de pe.
 */
export const DATA_INVALIDA = 'Data inválida';

/** `12/06/2025` */
export function formatarData(quando: Date): string {
  if (Number.isNaN(quando.getTime())) return DATA_INVALIDA;
  return DATA.format(quando);
}

/**
 * `12/06/2025` a partir do que o banco devolve: um dia (`aaaa-mm-dd`) ou um
 * instante. O dia puro NAO passa por `new Date(texto)`: isso e meia-noite UTC,
 * que no fuso de Brasilia ainda e o dia anterior — a validade de 01/10 saia
 * como 30/09. Nulo ou texto torto vira "Data inválida", nunca "Invalid Date".
 */
export function formatarDia(texto: string | null | undefined): string {
  if (!texto) return DATA_INVALIDA;
  const dia = /^\d{4}-\d{2}-\d{2}$/.test(texto) ? new Date(`${texto}T12:00:00`) : new Date(texto);
  return formatarData(dia);
}

/** `12/06/2025 · 14:32` — relogio sempre em 24 h. */
export function formatarDataHora(quando: Date): string {
  if (Number.isNaN(quando.getTime())) return DATA_INVALIDA;
  return `${DATA.format(quando)} · ${HORA.format(quando)}`;
}

/**
 * `14:32` — so a HORA, para quando a data ja esta escrita ao lado.
 *
 * Usa o MESMO formatador de `formatarDataHora` (relogio de 24 h, p.12): a hora
 * escrita de dois jeitos na mesma tela e como um sistema passa a parecer dois.
 */
export function formatarHora(quando: Date): string {
  if (Number.isNaN(quando.getTime())) return DATA_INVALIDA;
  return HORA.format(quando);
}

/**
 * `09/2026` — a competencia, que e um MES, a partir do `aaaa-mm-dd` do banco.
 *
 * Le o texto, e nao passa por `Date`: `new Date('2026-09-01')` e meia-noite
 * UTC, que no fuso de Brasilia ainda e 31/08, e a fatura de setembro sairia
 * como "08/2026". A tela de conciliacao imprimia o texto cru, "2026-09-01".
 */
export function formatarCompetencia(texto: string): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])/.test(texto)) return DATA_INVALIDA;
  return `${texto.slice(5, 7)}/${texto.slice(0, 4)}`;
}

/* ---------------------------------------------------------------------------
 * Do outro lado: o que a pessoa DIGITA.
 *
 * Formatar e so metade do trabalho. Quem lanca abastecimento digita "1.030" ou
 * "16,5", e `Number('16,5')` e `NaN`. Ler a entrada no formato do manual e a
 * mesma regra de sempre, na direcao contraria — e ela mora aqui, junto da
 * regra de escrita, para as duas nao divergirem.
 * ------------------------------------------------------------------------- */

/**
 * Le um numero escrito a mao, no formato brasileiro.
 *
 * `1.030` e mil e trinta; `16,5` e dezesseis e meio; `16.5` tambem e dezesseis
 * e meio, porque quem vem de teclado numerico ou de planilha em ingles digita
 * assim e o sistema nao deveria transformar isso em cento e sessenta e cinco.
 * A regra do ponto: separador de milhar so quando vier em grupos de tres
 * digitos. Qualquer outra coisa devolve `null` — chute e como um lancamento
 * vira dez vezes o que era.
 */
export function interpretarNumero(texto: string): number | null {
  const limpo = texto.trim().replace(/\s/g, '');
  if (!limpo || !/^[0-9.,]+$/.test(limpo)) return null;

  let normalizado: string;
  if (limpo.includes(',')) {
    normalizado = limpo.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) {
    normalizado = limpo.replace(/\./g, '');
  } else {
    normalizado = limpo;
  }

  const valor = Number(normalizado);
  return Number.isFinite(valor) ? valor : null;
}

/**
 * Numero -> texto de CAMPO para a pessoa editar: virgula decimal, ate duas
 * casas, sem separador de milhar (`1361,1`, `4832`). O inverso de
 * `interpretarNumero`. `String(1361.1)` escrevia "1361.1" com o ponto cru no
 * medidor inicial do celular (robos no QAS, 20/10/2026) — o formato que o
 * sistema ensina a ler como milhar.
 */
export function paraCampoNumero(valor: number): string {
  return String(Math.round(valor * 100) / 100).replace('.', ',');
}

/**
 * Instante -> valor de `<input type="datetime-local">`, que exige
 * `AAAA-MM-DDTHH:MM` em hora LOCAL.
 *
 * Nao da para usar `toISOString()` aqui: ela devolve UTC, e no fuso de Brasilia
 * um abastecimento das 7h da manha voltaria como 10h — tres horas de erro em
 * todo lancamento, silenciosas, e justo no campo que ninguem confere porque ja
 * veio preenchido.
 */
export function paraCampoDataHora(quando: Date): string {
  const dois = (numero: number) => String(numero).padStart(2, '0');
  return `${String(quando.getFullYear()).padStart(4, '0')}-${dois(quando.getMonth() + 1)}-${dois(quando.getDate())}T${dois(quando.getHours())}:${dois(quando.getMinutes())}`;
}

/** Valor de `<input type="datetime-local">` -> instante local. */
export function interpretarDataHora(texto: string): Date | null {
  if (!texto) return null;
  const quando = new Date(texto);
  return Number.isNaN(quando.getTime()) ? null : quando;
}
